// Send-to-customer recipient picker (2026-07-20). Answers the CEO's two
// asks: (1) a clear affordance to send an estimate / quote to the
// client, and (2) "copy his boss" — the rep picks WHO gets the share link.
//
// Checkbox list of the client's contacts (primary pre-checked) + an "add
// email" row for ad-hoc recipients (e.g. the client's boss). Submit shape
// (consumer-controlled — modal is presentation-only):
//   { recipients: Array<{ contact_id } | { email, name? }> }
// The consumer POSTs these to /orders/:id/send-for-approval, which emails
// EVERY recipient the same link. Mirrors the house modal idiom
// (CloneTargetModal / ConvertItemSelectionModal): brand palette, fixed
// overlay, ESC-to-close, footer Cancel + primary action.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent as ReactClipboardEvent,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bold,
  Check,
  Copy,
  Info,
  Italic,
  Link2,
  Paperclip,
  List,
  ListOrdered,
  Plus,
  RotateCcw,
  Underline,
  UserPlus,
  X,
} from "lucide-react";
import {
  deriveGreetingLine,
  isGreetingLine,
  type GreetingRecipient,
  isBuildingEmail,
} from "@merchd/shared";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api } from "@/lib/api";
import { ContactForm } from "@/components/contacts/ContactForm";
import { AddressField, withTypedAddress, type AddressBookEntry, type AddressEntry } from "@/components/send/AddressField";

export type SendRecipientInput =
  | { contact_id: string }
  | { email: string; name?: string };

/** Extra options carried alongside the recipient list (e.g. copy the rep). */
export interface SendExtraOptions {
  sendCopyToMe?: boolean;
  /**
   * The rep-authored FULL email body as HTML (CEO 2026-08-04: "the entire body
   * of the email should be editable — there shouldn't be a 'always added below
   * your message'"). This is the WHOLE email: greeting, prose, link line, help
   * line and sign-off. The server appends nothing below it, and sanitises it to
   * a strict allowlist before sending. `undefined` only when the body editor is
   * off (legacy callers).
   */
  bodyHtml?: string;
  /** Send PO window (To / CC / BCC, Stephanie 2026-10-14): set only when
   *  `addressBook` is on. The recipients list is the To line. */
  cc?: AddressEntry[];
  bcc?: AddressEntry[];
  /** The production mailbox BCC tick. */
  bccProduction?: boolean;
  /** The rep's subject line. */
  subject?: string;
  /** POs: attachments the rep took off this email (art file ids / virtual filenames). */
  leaveOff?: string[];
}

/** "2.4 MB" / "640 KB" — the artwork attachment sizes. */
function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Length cap for the editable body HTML — mirrors the server's zod cap. */
export const SEND_BODY_MAX = 20000;

/**
 * A pre-seeded, removable recipient shown in place of the client-contacts list.
 * Used by the PO send dialog: vendors have no client-contacts model, so the
 * caller seeds the vendor's primary email (and any others) as checkbox rows the
 * rep can uncheck. Each submits as an ad-hoc `{ email, name? }` recipient.
 */
export interface PresetRecipient {
  email: string;
  name?: string;
  /** Row title, e.g. the vendor name. */
  label: string;
  /** Second line under the title, e.g. "Primary supplier email". */
  sublabel?: string;
}

interface ContactRow {
  id: string;
  tags?: string[] | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  title: string | null;
  isPrimary: boolean | null;
}

interface ExtraEmail {
  email: string;
  name: string;
}

// Lightweight, dependency-free email shape check — matches the server's zod
// email() intent closely enough to keep obvious typos out of the chip list.
// The server re-validates authoritatively.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** The width the client preview is drawn at before it's scaled to fit. */
const PREVIEW_WIDTH = 1240;

// Paste normalisation (item 8) — MIRRORS the server allowlist in
// apps/api/src/services/html-sanitize.ts so the editor never shows formatting
// that will be stripped at send. <b>/<i> are folded to <strong>/<em>.
const PASTE_KEEP = new Set(["p", "br", "strong", "em", "u", "ul", "ol", "li", "a"]);
// Dropped along with their content (payload carriers, not prose).
const PASTE_DROP_CONTENT = new Set([
  "script",
  "style",
  "head",
  "title",
  "meta",
  "link",
  "iframe",
  "object",
  "embed",
  "svg",
  "noscript",
  "template",
]);
// Not kept, but they still imply a paragraph break.
const PASTE_BLOCK = new Set([
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "tr",
  "section",
  "article",
]);

/**
 * The BASELINE note: one authored email goes to every recipient. Deliberately
 * says nothing about the greeting — with 0 or 1 recipients there is nothing to
 * warn about, and on the PO surface there is nothing to promise (CEO 2026-08-05:
 * "PO's do not need a name", and vendor presets never contribute one). Round
 * 66's wording claimed the greeting named nobody, which is no longer true; a
 * sentence the behaviour contradicts is worse than no sentence, so the claim is
 * gone rather than reworded. The 2+-recipient case gets its own line (see
 * `sendNote`). Rendered under the recipient list, NOT in the header subtitle
 * (which it pushed to three lines).
 */
const SEND_ONE_EMAIL_NOTE = "Everyone you pick below gets this one email.";

/** What the editor (and the server) will actually keep. Rides the toolbar as a
 *  tooltip rather than a second caption line — the paste normalisation already
 *  makes the editor honest, so this is belt-and-braces, not load-bearing copy,
 *  and the dialog needs its vertical space back more than it needs the line. */
const FORMATTING_CAPTION =
  "Bold, italics, underline, lists and links are kept — other formatting is removed.";

/**
 * ONE string per blocking state (item 7). The footer status, the disabled
 * Send's tooltip and the on-body message all read from here, so they are
 * byte-identical BY CONSTRUCTION rather than by two authors agreeing. Restating
 * a message the rep can already see, in different words, reads as two problems.
 *
 * `noRecipients` is the only state with no on-body twin, so it is the only one
 * whose wording exists solely in the footer.
 */
const BLOCK_COPY = {
  composeFailed: "Couldn't load the default email",
  composeLoading: "Loading the default email…",
  bodyEmpty: "The email body is empty — write something, or press Reset to default.",
  bodyTooLong: "Too long — please shorten it.",
  noRecipients: "Pick at least one recipient.",
  noToRecipient: "Add someone on the To line.",
  noSubject: "Write a subject.",
} as const;

/**
 * Write `line` into the body's greeting paragraph, returning EXACTLY what was
 * written — or null when there is no paragraph we may own.
 *
 * We only take a first paragraph that is (a) plain text, with no elements the
 * rep has added inside it, and (b) already reads as a greeting. So we never
 * invent a greeting where the rep deleted one, and never flatten formatting
 * someone applied. The return value is what the caller records as "ours"; see
 * the ownership note on `greetingOwned`.
 */
function seedGreeting(el: HTMLDivElement, line: string): string | null {
  const first = el.firstElementChild;
  if (!first || first.tagName !== "P" || first.children.length > 0) return null;
  if (!isGreetingLine(first.textContent ?? "")) return null;
  first.textContent = line;
  return line;
}

function contactLabel(c: ContactRow): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return name.length > 0 ? name : (c.email ?? "Contact");
}

export function SendToCustomerModal(props: {
  /** Modal title, e.g. "Send Estimate #123" / "Send Quote #123". */
  title: string;
  /** Short line under the title describing what will be sent. */
  subtitle: string;
  /** Client whose contacts populate the picker. Ignored in vendor mode
   *  (when `presetRecipients` is provided) — pass "" there. */
  clientId: string;
  /**
   * Vendor mode (PO send): when provided, these pre-seeded emails REPLACE the
   * client-contacts checkbox list (the contacts query is skipped). All start
   * checked; the rep can uncheck any (e.g. drop the vendor primary) and still
   * add more via "Also send to". Each checked entry submits as { email, name? }.
   */
  presetRecipients?: PresetRecipient[];
  /** Heading over the recipient list. Default "Client contacts" (or
   *  "Recipients" in vendor mode). */
  contactsLabel?: string;
  /** Label for the primary action, e.g. "Send Estimate" / "Send & Request Payment". */
  ctaLabel: string;
  /**
   * Visual weight of the primary action. "destructive" (rust) is for the ARMED
   * second click of a soft gate — the $0-decoration confirm and the missing-link
   * confirm both use it. Without this the armed CTA is the same peach button in
   * the same place as an ordinary Send, so a rep who reflexively clicks twice
   * blows straight through the confirm. Default "primary".
   */
  ctaVariant?: "primary" | "destructive";
  submitting: boolean;
  /** Genuine failures ONLY (validation, mail errors) — rendered rust. Soft,
   *  non-blocking warnings belong in `noticeMessage`. */
  errorMessage: string | null;
  /**
   * Soft, non-blocking warning (e.g. the PO $0-decoration confirm: "click Send
   * PO anyway to proceed"). Rendered as the house peach warning box — visually
   * distinct from `errorMessage` so a confirmation prompt never reads as a
   * failure.
   */
  noticeMessage?: string | null;
  /** Show a "Send me a copy" checkbox (adds the logged-in rep as a recipient). */
  showCopyToMe?: boolean;
  /**
   * Allow turning an ad-hoc email into a saved client contact via a popup that
   * reuses the full contact form. Off by default so the estimate/SO flows are
   * byte-for-byte unchanged.
   */
  allowAddContact?: boolean;
  /**
   * Show the FULL-BODY rich editor (CEO 2026-08-04). Off by default so any
   * modal that doesn't opt in is unchanged. When on, the dialog fetches the
   * document's default email from `composeUrl`, drops the whole thing —
   * greeting, prose, link line, help line, sign-off — into an editable rich
   * text box, and submits it as `bodyHtml`. Nothing is appended server-side.
   *
   * This SUPERSEDES the round-59 "message + always-added-below" editor: the CEO
   * killed the fixed frame outright.
   */
  showBodyEditor?: boolean;
  /**
   * GET endpoint returning `{ subject, body_html, share_url, share_uuid }` —
   * the document's default email, rendered by the same server template the send
   * route uses, so an untouched body sends exactly what it sends today.
   * Required when `showBodyEditor` is on.
   */
  composeUrl?: string;
  /** Editor label, e.g. "Email to the client" (default) or a vendor variant. */
  bodyLabel?: string;
  /** What the "Add link" / "Copy link" buttons attach, e.g. "presentation
   *  link". Copy only — the URL itself comes from `composeUrl`'s response. */
  linkNoun?: string;
  /**
   * The logged-in user's email. Used ONLY to explain the "Sending as" line when
   * the resolved sender isn't them — a rep sending on a colleague's project
   * sees their colleague's name on the From with no idea why. Optional; when
   * omitted the line renders without the explanation.
   */
  currentUserEmail?: string | null;
  /**
   * Send PO window like commonsku (Stephanie 2026-10-14, Jake approved): To,
   * CC and BCC lines plus an editable subject, ONE email. These are the
   * supplier's contacts offered on each line. Vendor mode only; the presets
   * start on To, the supplier's art contact on CC, production ticked on BCC.
   */
  addressBook?: AddressBookEntry[];
  /** Send PO window (Stephanie 2026-10-15): GET endpoint of the saved
   *  templates ({ available, templates }). Picking one re-fetches
   *  `composeUrl?template_id=…` and replaces the email + subject. */
  templatesUrl?: string;
  /** What the client will see, shown beside the email (Stephanie
   *  2026-10-26: "a preview of the quote the client will see when sending").
   *  Look-only: clicks inside it do nothing. */
  preview?: ReactNode;
  /** A Subject box above the email (Stephanie 2026-10-27: "edit the subject
   *  line … if we want to add in that it's a revised quote"). Seeded from the
   *  compose endpoint's `subject`; sent as options.subject. */
  editableSubject?: boolean;
  /** Heading over the typed-in addresses. The quote send puts them on CC of
   *  one email, so it says "CC" (Stephanie 2026-10-27). */
  extrasLabel?: string;
  onCancel: () => void;
  onSubmit: (
    recipients: SendRecipientInput[],
    options?: SendExtraOptions,
  ) => void;
}) {
  const {
    title,
    subtitle,
    clientId,
    presetRecipients,
    contactsLabel,
    ctaLabel,
    ctaVariant = "primary",
    submitting,
    errorMessage,
    noticeMessage = null,
    showCopyToMe = false,
    allowAddContact = false,
    showBodyEditor = false,
    composeUrl,
    bodyLabel = "Email to the client",
    linkNoun = "link",
    currentUserEmail = null,
    addressBook,
    templatesUrl,
    preview,
    editableSubject = false,
    extrasLabel = "Also send to",
    onCancel,
    onSubmit,
  } = props;

  // Scale the client preview to the pane's width (minus a scrollbar).
  const [previewPane, setPreviewPane] = useState<HTMLDivElement | null>(null);
  const [previewZoom, setPreviewZoom] = useState(0.66);
  useEffect(() => {
    if (!previewPane || typeof ResizeObserver === "undefined") return;
    const fit = () => setPreviewZoom(Math.max(0.4, Math.min(1, (previewPane.clientWidth - 2) / PREVIEW_WIDTH)));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(previewPane);
    return () => ro.disconnect();
  }, [previewPane]);

  // Vendor mode (PO send): pre-seeded emails stand in for client contacts.
  const vendorMode = presetRecipients !== undefined;
  const presets = useMemo(() => presetRecipients ?? [], [presetRecipients]);

  const contactsQ = useQuery({
    queryKey: ["clients", clientId, "contacts"],
    queryFn: () =>
      api<{ contacts: ContactRow[] }>(`/clients/${clientId}/contacts`),
    enabled: clientId.length > 0 && !vendorMode,
  });

  const contacts = useMemo(
    () => contactsQ.data?.contacts ?? [],
    [contactsQ.data],
  );
  // Only contacts with an email can be emailed the link.
  const emailable = useMemo(
    () => contacts.filter((c) => (c.email ?? "").trim().length > 0),
    [contacts],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Vendor-mode preset selection, keyed by lowercased email. Seeded checked so
  // the vendor primary is a one-click send; the rep can uncheck any of them.
  const [selectedPresets, setSelectedPresets] = useState<Set<string>>(
    () => new Set(presets.map((p) => p.email.toLowerCase())),
  );
  const [initialised, setInitialised] = useState(false);
  const [extras, setExtras] = useState<ExtraEmail[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  // Ticked by default (Stephanie 2026-11-02: "send me a copy should be checked by default").
  // Only where the box is shown (quote, invoice, presentation, the PO window);
  // a send window without the box never sends a hidden copy.
  const [copyToMe, setCopyToMe] = useState(showCopyToMe || (vendorMode && addressBook !== undefined));
  // ── Send PO window: To / CC / BCC + subject (Stephanie 2026-10-14) ──────
  const addressMode = vendorMode && addressBook !== undefined;
  const book = useMemo(() => addressBook ?? [], [addressBook]);
  const [toList, setToList] = useState<AddressEntry[]>(() =>
    presets.map((p) => ({ email: p.email, ...(p.name ? { name: p.name } : {}) })),
  );
  const [ccList, setCcList] = useState<AddressEntry[]>([]);
  const [bccList, setBccList] = useState<AddressEntry[]>([]);
  // What's typed in each box but not yet Added — it goes on Send too
  // (Stephanie 2026-10-28, same rule as the quote window).
  const [drafts, setDrafts] = useState<{ to: string; cc: string; bcc: string }>({ to: "", cc: "", bcc: "" });
  const [draftErrors, setDraftErrors] = useState<{ to?: string; cc?: string; bcc?: string }>({});
  const setDraft = (k: "to" | "cc" | "bcc") => (v: string): void => {
    setDrafts((d) => ({ ...d, [k]: v }));
    setDraftErrors((e) => ({ ...e, [k]: undefined }));
  };
  const [bccProduction, setBccProduction] = useState(true);
  // POs: attachments taken off this one email (Stephanie 2026-11-02: "let the
  // rep unattach one"). Keys: art file ids and virtual filenames.
  const [leaveOff, setLeaveOff] = useState<ReadonlySet<string>>(new Set());
  const toggleLeaveOff = (key: string): void =>
    setLeaveOff((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const [subject, setSubject] = useState<string | null>(null);
  // Index of the extra-email chip currently being turned into a saved contact
  // via the popup (null = popup closed). Only used when allowAddContact.
  const [addContactIdx, setAddContactIdx] = useState<number | null>(null);
  // ── Full-body rich editor (CEO 2026-08-04) ───────────────────────────────
  // The dialog opens pre-filled with the ENTIRE email — greeting, prose, link
  // line, help line, sign-off — and every word of it is editable. The default
  // comes from the SERVER (composeUrl), rendered by the same template the send
  // route uses, so a rep who edits nothing sends exactly what they send today.
  const composeQ = useQuery({
    queryKey: ["send-compose", composeUrl],
    queryFn: () =>
      api<{
        subject: string;
        body_html: string;
        share_url: string;
        share_uuid: string;
        /** Who this will visibly come FROM — resolved by the SAME code path
         *  that sets the real From header, including the noreply@ fallback. */
        from_name: string;
        from_email: string;
        /** POs only: the supplier's art team, CC'd (artwork links in the body). */
        art_cc?: string | null;
        /** POs only: the production mailbox (the BCC tick), null = not set up. */
        production_email?: string | null;
        /** POs only: artwork files attached to the email (false = too big —
         *  the download link in the email covers it). */
        artwork_files?: Array<{ file_id: string; filename: string; size_bytes: number; attached: boolean }>;
        /** The item virtuals attached too (Stephanie 2026-10-27). */
        picture_files?: Array<{ filename: string }>;
      }>(composeUrl as string),
    enabled: showBodyEditor && !!composeUrl,
    // The share link + rep signature don't change while the dialog is open, and
    // a refetch would clobber the rep's in-progress edits.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  // Seed the subject and the supplier's art contact on CC once, when the
  // default email arrives — then they're the rep's to change.
  const [addressSeeded, setAddressSeeded] = useState(false);
  useEffect(() => {
    if (!addressMode || addressSeeded || !composeQ.data) return;
    setAddressSeeded(true);
    setSubject((s) => s ?? composeQ.data.subject);
    const art = composeQ.data.art_cc;
    if (art && !toList.some((t) => t.email.toLowerCase() === art.toLowerCase())) {
      const hit = book.find((b) => b.email.toLowerCase() === art.toLowerCase());
      setCcList([{ email: art, ...(hit?.name ? { name: hit.name } : {}) }]);
    }
  }, [addressMode, addressSeeded, composeQ.data, toList, book]);
  const takenEmails = useMemo(
    () => new Set([...toList, ...ccList, ...bccList].map((a) => a.email.toLowerCase())),
    [toList, ccList, bccList],
  );
  // Client mode with an editable subject: seed it once from the default email.
  useEffect(() => {
    if (addressMode || !editableSubject || !composeQ.data?.subject) return;
    setSubject((s) => s ?? composeQ.data.subject);
  }, [addressMode, editableSubject, composeQ.data]);
  const subjectBad =
    (addressMode && addressSeeded && (subject ?? "").trim() === "") ||
    (!addressMode && editableSubject && subject !== null && subject.trim() === "");
  // Saved templates (Stephanie 2026-10-15). Hidden until Jake's database
  // update has run (available: false) or when there are none yet.
  const templatesQ = useQuery({
    queryKey: ["send-templates", templatesUrl],
    queryFn: () =>
      api<{ available: boolean; templates: Array<{ id: string; name: string }> }>(templatesUrl as string),
    enabled: addressMode && !!templatesUrl,
    staleTime: 60_000,
  });
  const [templateId, setTemplateId] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [bodySeeded, setBodySeeded] = useState(false);
  const [bodyDirty, setBodyDirty] = useState(false);
  /**
   * The greeting paragraph THIS DIALOG last wrote, byte-exact — or null once we
   * don't own it (never did, or the rep's own words are in that line now).
   *
   * This, and NOT `bodyDirty`, is the may-I-rewrite test. `bodyDirty` flips on
   * any edit anywhere in the body — bold a word in paragraph three, press "Add
   * link", paste a sentence — and gating the greeting on it meant that after any
   * such edit the rep could uncheck Amy, check Bob, and still send Bob an email
   * opening "Hi Amy,". That is round 66's wrong-name bug with a new trigger, and
   * the trigger is the whole point of round 66. An exact match against what we
   * last wrote is the only test that distinguishes "the rep edited THIS line"
   * from "the rep edited the body".
   *
   * It is also what makes a deliberate customisation safe: a rep who types
   * "Hi Amy — hope you're well," has changed the line away from ours, so we let
   * go permanently. A "does it still look like a greeting?" test would happily
   * overwrite that sentence.
   *
   * State rather than a ref because the 2+-recipient note quotes it, and a note
   * quoting a name the body doesn't contain would be the same class of lie the
   * greeting itself is here to avoid.
   */
  const [greetingOwned, setGreetingOwned] = useState<string | null>(null);
  const [bodyOverLimit, setBodyOverLimit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkHint, setLinkHint] = useState<string | null>(null);
  // Which formatting marks apply at the caret, so the toolbar can show pressed
  // state instead of every icon reading "off" inside bold text.
  const [activeMarks, setActiveMarks] = useState<Record<string, boolean>>({});
  // Is the body empty once markup is stripped? A rep can select-all-delete after
  // a SUCCESSFUL compose and send a blank email; the missing-link gate would
  // catch that only incidentally, and its copy talks about the link, not
  // emptiness. This is the explicit guard.
  const [bodyEmpty, setBodyEmpty] = useState(false);
  const shareUrl = composeQ.data?.share_url ?? "";

  /**
   * The greeting line for the CURRENT selection (CEO 2026-08-05). Derived, never
   * cached: round 66's bug was a name frozen at dialog-open that the selection
   * then drifted away from, putting a non-recipient's name on a real email.
   * Because this is a `useMemo` over the live selection state, the name can only
   * ever be someone the rep is actually sending to.
   *
   * Order matches what the rep reads top-down — the checkbox list first, then
   * the "Also send to" chips — so "the first recipient" means the same thing to
   * the code and to the human.
   *
   * Vendor presets contribute NO name (CEO 2026-08-05: "PO's do not need a
   * name"). Their label is a COMPANY — first-token-of-a-company would greet a
   * supplier "Hi Acme," off "Acme Printing Inc" — so those sends stay
   * "Hi there," and the rep types a buyer's name if they want one.
   */
  const greetingLine = useMemo(() => {
    const chosen: GreetingRecipient[] = vendorMode
      ? presets
          .filter((p) => selectedPresets.has(p.email.toLowerCase()))
          .map(() => ({}))
      : emailable.filter((c) => selected.has(c.id)).map((c) => ({ ...c, buildingEmail: isBuildingEmail(c.tags) }));
    return deriveGreetingLine([
      ...chosen,
      ...extras.map((e) => ({ name: e.name })),
    ]);
  }, [vendorMode, presets, selectedPresets, emailable, selected, extras]);

  /**
   * Why the From isn't the person clicking Send (item 10). Silence is fine when
   * they match; when they don't, the rep needs to know whose name goes out
   * BEFORE they send, not after a client replies to a colleague. Compared on
   * email rather than name because two people can share a display name.
   */
  const sendingAsReason: string | null = (() => {
    const fromEmail = composeQ.data?.from_email?.trim().toLowerCase();
    if (!fromEmail) return null;
    const me = currentUserEmail?.trim().toLowerCase();
    if (me && fromEmail === me) return null;
    // The server fell back because the rep's row is gone or has no mailbox —
    // say that, rather than claiming a rep who isn't there.
    if (fromEmail.startsWith("noreply@")) {
      return "no rep mailbox on file — replies come back to other";
    }
    // POs go out from the production mailbox (Stephanie 2026-10-14).
    if (vendorMode) return "POs go out from the production mailbox";
    return me ? "the account rep on this project" : null;
  })();

  // Seed the editor ONCE, when the default arrives. Re-seeding on every render
  // would fight the rep's cursor; contenteditable is uncontrolled by design.
  //
  // The greeting is written as PART OF the seed, not as a follow-up pass: a
  // seed-then-rewrite leaves a frame where the server's generic "Hi there,"
  // paints before the real name lands, which reads as a glitch and, worse,
  // makes the name look like an afterthought the rep might not trust.
  useEffect(() => {
    const el = editorRef.current;
    if (!showBodyEditor || bodySeeded || !el || !composeQ.data) return;
    el.innerHTML = composeQ.data.body_html;
    setGreetingOwned(seedGreeting(el, greetingLine));
    setBodySeeded(true);
    // greetingLine is read once here; the effect below keeps it current for the
    // rest of the dialog's life. Depending on it would re-run the SEED.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBodyEditor, bodySeeded, composeQ.data]);

  /** True when the editor holds no words — markup stripped, entities collapsed. */
  const isEditorEmpty = (el: HTMLDivElement | null): boolean =>
    ((el?.innerHTML ?? "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim().length === 0);

  /** Recompute every derived editor flag after any mutation of the body. */
  const syncEditorState = useCallback((): void => {
    const el = editorRef.current;
    setBodyOverLimit((el?.innerHTML.length ?? 0) > SEND_BODY_MAX);
    setBodyEmpty(isEditorEmpty(el));
  }, []);

  /**
   * Keep the greeting in step with the recipient picker, for as long as the
   * greeting line is still OURS — byte-identical to what we last wrote, with no
   * formatting the rep added inside it.
   *
   * Gating this on whole-body dirtiness (the first cut) meant ANY edit anywhere
   * froze the greeting, so a rep who bolded a word in paragraph three and then
   * swapped recipients shipped the previous person's name — round 66's bug via
   * the very feature round 66 shipped. An edit ELSEWHERE must never freeze the
   * greeting; an edit to the greeting line must stop us permanently. Exact match
   * is what tells those two apart.
   */
  useEffect(() => {
    const el = editorRef.current;
    if (!showBodyEditor || !bodySeeded || !el) return;
    if (greetingOwned === null || greetingOwned === greetingLine) return;
    const first = el.firstElementChild;
    if (
      first === null ||
      first.tagName !== "P" ||
      first.children.length > 0 ||
      first.textContent !== greetingOwned
    ) {
      // The rep's own words are in that line now. Hands off — permanently.
      setGreetingOwned(null);
      return;
    }
    first.textContent = greetingLine;
    setGreetingOwned(greetingLine);
    syncEditorState();
  }, [showBodyEditor, bodySeeded, greetingOwned, greetingLine, syncEditorState]);

  /**
   * Reflect the caret's formatting in the toolbar (item 10). queryCommandState
   * is the companion to execCommand — same deprecated-but-universal API, no new
   * dependency — and without it every icon reads "off" even inside bold text.
   */
  const MARK_COMMANDS = [
    "bold",
    "italic",
    "underline",
    "insertUnorderedList",
    "insertOrderedList",
  ] as const;
  const refreshMarks = useCallback((): void => {
    const next: Record<string, boolean> = {};
    for (const cmd of MARK_COMMANDS) {
      try {
        next[cmd] = document.queryCommandState(cmd);
      } catch {
        next[cmd] = false;
      }
    }
    setActiveMarks(next);
    // MARK_COMMANDS is a module-stable literal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetBody = useCallback((): void => {
    const el = editorRef.current;
    if (!el || !composeQ.data) return;
    el.innerHTML = composeQ.data.body_html;
    // Reset RE-TAKES the greeting: the rep just asked for the default back, so
    // a line we'd previously let go of is ours again — and it comes back with
    // the CURRENT selection's name, not the one from when the dialog opened.
    setGreetingOwned(seedGreeting(el, greetingLine));
    setBodyDirty(false);
    setBodyOverLimit(false);
    setBodyEmpty(false);
    setLinkHint(null);
  }, [composeQ.data, greetingLine]);

  /** Swap the email + subject for a saved template (or back to the
   *  standard email). The server fills the PO's details in. */
  const applyTemplate = async (id: string): Promise<void> => {
    if (!composeUrl) return;
    setTemplateId(id);
    setTemplateError(null);
    setTemplateLoading(true);
    try {
      const out = await api<{ subject: string; body_html: string }>(
        id ? `${composeUrl}?template_id=${encodeURIComponent(id)}` : composeUrl,
      );
      const el = editorRef.current;
      // POs greet "Hi there," whoever is on it, so there's no name to keep
      // in step with the To line here.
      if (el) el.innerHTML = out.body_html;
      setGreetingOwned(null);
      setSubject(out.subject);
      setBodyDirty(false);
      setBodyOverLimit(false);
      setBodyEmpty(false);
      setLinkHint(null);
    } catch {
      setTemplateError("Couldn't load that template — try again.");
    } finally {
      setTemplateLoading(false);
    }
  };

  /**
   * Apply a formatting command to the current selection. `document.execCommand`
   * is deprecated-but-universal and needs no dependency; the house has no rich
   * text editor to reuse, and the output is re-sanitised server-side against a
   * strict allowlist regardless of what the browser produces. onMouseDown
   * preventDefault keeps the selection alive while the button takes focus.
   */
  const exec = useCallback(
    (command: string, value?: string): void => {
      editorRef.current?.focus();
      document.execCommand(command, false, value);
      setBodyDirty(true);
      syncEditorState();
      refreshMarks();
    },
    [syncEditorState, refreshMarks],
  );

  /**
   * "Add link" — attach the document's share URL to the selected words (CEO:
   * "you can just click the word and add a link"). With nothing selected we
   * insert a labelled link at the cursor rather than doing nothing silently.
   */
  const attachLink = useCallback((): void => {
    if (shareUrl.length === 0) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    const hasSelection =
      sel && sel.rangeCount > 0 && sel.toString().trim().length > 0;
    if (hasSelection) {
      document.execCommand("createLink", false, shareUrl);
      setLinkHint("Link added to the selected text.");
    } else {
      document.execCommand(
        "insertHTML",
        false,
        `<a href="${shareUrl}">click here</a>`,
      );
      setLinkHint(
        "Nothing was selected, so we inserted a “click here” link at your cursor.",
      );
    }
    setBodyDirty(true);
  }, [shareUrl]);

  /**
   * Paste is normalised to the allowlist AT PASTE TIME (item 8). Without this,
   * pasting from Word / Outlook shows headings, colours and tables live in the
   * editor and then silently loses them at send — the editor would be lying
   * about what the client receives. We keep the same seven tags the server
   * keeps (bold/italic/underline/lists/links) and drop the rest to plain text,
   * so what the rep sees is what ships. The server still re-sanitises; this is
   * honesty in the UI, never the security boundary.
   */
  const onPaste = useCallback(
    (e: ReactClipboardEvent<HTMLDivElement>): void => {
      const html = e.clipboardData.getData("text/html");
      const text = e.clipboardData.getData("text/plain");
      if (!html && !text) return;
      e.preventDefault();
      let insert: string;
      if (html) {
        // Parse in an inert document so nothing in the pasted markup executes.
        const doc = new DOMParser().parseFromString(html, "text/html");
        const walk = (node: Node): string => {
          if (node.nodeType === Node.TEXT_NODE) {
            return (node.textContent ?? "")
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;");
          }
          if (node.nodeType !== Node.ELEMENT_NODE) return "";
          const el = node as HTMLElement;
          const tag = el.tagName.toLowerCase();
          if (PASTE_DROP_CONTENT.has(tag)) return "";
          const inner = Array.from(el.childNodes).map(walk).join("");
          if (tag === "br") return "<br>";
          if (tag === "b") return `<strong>${inner}</strong>`;
          if (tag === "i") return `<em>${inner}</em>`;
          if (tag === "a") {
            const href = el.getAttribute("href") ?? "";
            return /^(https?:\/\/|mailto:)/i.test(href)
              ? `<a href="${href.replace(/"/g, "&quot;")}">${inner}</a>`
              : inner;
          }
          if (PASTE_KEEP.has(tag)) return `<${tag}>${inner}</${tag}>`;
          // Block-level tags we don't keep still need their line break.
          return PASTE_BLOCK.has(tag) ? `<p>${inner}</p>` : inner;
        };
        insert = Array.from(doc.body.childNodes).map(walk).join("");
      } else {
        insert = text
          .split(/\n{2,}/)
          .map(
            (para) =>
              `<p>${para
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/\n/g, "<br>")}</p>`,
          )
          .join("");
      }
      document.execCommand("insertHTML", false, insert);
      setBodyDirty(true);
      syncEditorState();
    },
    [syncEditorState],
  );

  const copyLink = useCallback((): void => {
    if (shareUrl.length === 0) return;
    void navigator.clipboard?.writeText(shareUrl).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  }, [shareUrl]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  // Pre-check the primary contact (or the sole emailable contact) once the
  // list loads — the common case is "send to the primary", so make it one
  // click. Runs once so the rep's manual de-selection isn't clobbered.
  useEffect(() => {
    if (initialised || emailable.length === 0) return;
    const primary = emailable.find((c) => c.isPrimary);
    const seed = primary ?? (emailable.length === 1 ? emailable[0] : null);
    setSelected(seed ? new Set([seed.id]) : new Set());
    setInitialised(true);
  }, [emailable, initialised]);

  // Close on ESC (fixed overlay).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submitting, onCancel]);

  const alreadyChosenEmails = useMemo(() => {
    const set = new Set<string>();
    for (const c of emailable) {
      if (selected.has(c.id) && c.email) set.add(c.email.toLowerCase());
    }
    for (const p of presets) {
      if (selectedPresets.has(p.email.toLowerCase())) {
        set.add(p.email.toLowerCase());
      }
    }
    for (const e of extras) set.add(e.email.toLowerCase());
    return set;
  }, [emailable, selected, presets, selectedPresets, extras]);

  const addExtra = (): void => {
    const email = newEmail.trim();
    if (!EMAIL_RE.test(email)) {
      setAddError("Enter a valid email address.");
      return;
    }
    if (alreadyChosenEmails.has(email.toLowerCase())) {
      setAddError("That address is already on the list.");
      return;
    }
    setExtras((prev) => [...prev, { email, name: newName.trim() }]);
    setNewEmail("");
    setNewName("");
    setAddError(null);
  };

  const typedTo = withTypedAddress(toList, drafts.to, book, takenEmails);
  const recipientCount = addressMode
    ? "entries" in typedTo ? typedTo.entries.length : toList.length + 1
    : (vendorMode ? selectedPresets.size : selected.size) +
      extras.length +
      // A valid address typed but not yet Added counts — it goes on Send.
      (EMAIL_RE.test(newEmail.trim()) && !alreadyChosenEmails.has(newEmail.trim().toLowerCase()) ? 1 : 0);

  /**
   * The note under the recipient list, scaled to what is actually at stake.
   * THREE states, because the risk isn't constant:
   *
   * 1. Plain — 0–1 recipients (one email, one reader, nothing to warn about), and
   *    vendor mode at any count (CEO 2026-08-05: "PO's do not need a name" —
   *    presets contribute none, so any greeting promise here would be a lie).
   *    A caveat in these states is noise the rep learns to skip past.
   * 2. 2+ recipients, greeting still OURS — quote the actual line. Abstract
   *    wording doesn't land; the rep needs to connect the warning to the text
   *    sitting in the box. The quote is `greetingOwned` (what this dialog wrote)
   *    and never the derived `greetingLine`, so it can't name someone the email
   *    doesn't greet.
   * 3. 2+ recipients, rep has written their OWN greeting — warn WITHOUT quoting.
   *    This is the peak-risk state, not a safe one: a hand-written greeting is
   *    the most likely to be recipient-specific ("Hi Amy — great catching up on
   *    our call,"), which is the worst thing for Bob and Carol to receive. An
   *    earlier cut fell back to the plain note here, so the warning vanished
   *    exactly when it mattered most. We keep the discipline that caused that
   *    (never quote a string we can't see) and drop only the quote, not the
   *    warning.
   */
  const sendNote =
    vendorMode || recipientCount < 2
      ? SEND_ONE_EMAIL_NOTE
      : greetingOwned === null
        ? `Sending to ${recipientCount} people — you've edited the greeting, so check it still makes sense for all of them.`
        : `Sending to ${recipientCount} people — this opens with “${greetingOwned}” for all of them. Edit the greeting below if that's not right.`;
  // "Send me a copy" adds the rep server-side, so it's a valid send on its own.
  // With the body editor on we also wait for the default body to load — sending
  // an empty email because the compose fetch hadn't landed would be a real bug.
  const bodyReady = !showBodyEditor || bodySeeded;
  const bodyBlank = showBodyEditor && bodySeeded && bodyEmpty;
  const canSubmit =
    (recipientCount > 0 || (copyToMe && !addressMode)) &&
    !subjectBad &&
    !submitting &&
    !bodyOverLimit &&
    !bodyBlank &&
    bodyReady;

  /**
   * Why the Send button is disabled, in plain words. A disabled primary action
   * with no explanation is a dead end — this rides as the button's `title` and,
   * for the blocking cases, as visible copy under the editor.
   */
  const blockedReason: string | null = submitting
    ? null
    : composeQ.isError
      ? BLOCK_COPY.composeFailed
      : !bodyReady
        ? BLOCK_COPY.composeLoading
        : bodyBlank
          ? BLOCK_COPY.bodyEmpty
          : bodyOverLimit
            ? BLOCK_COPY.bodyTooLong
            : recipientCount === 0 && (!copyToMe || addressMode)
              ? addressMode
                ? BLOCK_COPY.noToRecipient
                : BLOCK_COPY.noRecipients
              : subjectBad
                ? BLOCK_COPY.noSubject
                : null;

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    // Send PO window: addresses typed in To / CC / BCC but not Added go too;
    // refuse to send if a box holds something that isn't an address.
    let sendTo = toList;
    let sendCc = ccList;
    let sendBcc = bccList;
    if (addressMode) {
      const t = withTypedAddress(toList, drafts.to, book, takenEmails);
      const takenWithTo = new Set([...takenEmails, ...("entries" in t ? t.entries : []).map((a) => a.email.toLowerCase())]);
      const c = withTypedAddress(ccList, drafts.cc, book, takenWithTo);
      const takenWithCc = new Set([...takenWithTo, ...("entries" in c ? c.entries : []).map((a) => a.email.toLowerCase())]);
      const b = withTypedAddress(bccList, drafts.bcc, book, takenWithCc);
      if ("error" in t || "error" in c || "error" in b) {
        const fix = (r: typeof t) => ("error" in r ? `${r.error} Or clear the box before sending.` : undefined);
        setDraftErrors({ to: fix(t), cc: fix(c), bcc: fix(b) });
        return;
      }
      sendTo = t.entries;
      sendCc = c.entries;
      sendBcc = b.entries;
    }
    // An address typed in "Also send to" but never Added used to be dropped
    // without a word (Stephanie 2026-10-27: "I CC'ed Jake … he never received
    // it"). Send it too; refuse to send if what's typed isn't an address.
    const typed = newEmail.trim();
    if (typed !== "" && !EMAIL_RE.test(typed)) {
      setAddError("Enter a valid email address — or clear the box — before sending.");
      return;
    }
    const pendingExtra: ExtraEmail[] =
      typed !== "" && !alreadyChosenEmails.has(typed.toLowerCase())
        ? [{ email: typed, name: newName.trim() }]
        : [];
    // Vendor mode submits checked presets as ad-hoc emails; client mode submits
    // the checked contacts by id. Extras (typed-in emails) apply to both.
    const chosenPresets: SendRecipientInput[] = vendorMode
      ? presets
          .filter((p) => selectedPresets.has(p.email.toLowerCase()))
          .map((p) =>
            p.name && p.name.length > 0
              ? { email: p.email, name: p.name }
              : { email: p.email },
          )
      : [];
    const recipients: SendRecipientInput[] = addressMode ? sendTo : [
      ...(vendorMode
        ? chosenPresets
        : Array.from(selected).map((id) => ({ contact_id: id }))),
      ...[...extras, ...pendingExtra].map((e) =>
        e.name.length > 0 ? { email: e.email, name: e.name } : { email: e.email },
      ),
    ];
    onSubmit(recipients, {
      sendCopyToMe: copyToMe,
      // The WHOLE email, always — edited or not. There is no "unchanged →
      // omit" branch any more: what the rep sees in the box IS what sends.
      bodyHtml: showBodyEditor
        ? (editorRef.current?.innerHTML ?? "")
        : undefined,
      ...(addressMode
        ? {
            cc: sendCc,
            bcc: sendBcc,
            bccProduction: Boolean(composeQ.data?.production_email) && bccProduction,
            subject: (subject ?? "").trim() || undefined,
            ...(leaveOff.size > 0 ? { leaveOff: [...leaveOff] } : {}),
          }
        : editableSubject && subject !== null
          ? { subject: subject.trim() }
          : {}),
    });
  };

  // A new contact was saved from the "Add this person as a contact" popup:
  // drop the one-off chip (it's a real contact now), pre-check the new contact
  // (the contacts query refetches via ContactForm's invalidation), and close.
  const onContactCreated = (contact: { id: string }): void => {
    setExtras((prev) =>
      prev.filter((_, idx) => idx !== addContactIdx),
    );
    setSelected((prev) => new Set(prev).add(contact.id));
    setAddContactIdx(null);
  };

  // Split a free-form "First Last" into the popup's first/last prefill.
  const splitName = (name: string): { first: string; last: string } => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { first: "", last: "" };
    return { first: parts[0] ?? "", last: parts.slice(1).join(" ") };
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-customer-modal-title"
    >
      <div
        className={
          preview
            ? "flex h-[90vh] w-full max-w-[1500px] overflow-hidden rounded-lg border border-brown-100 bg-white shadow-xl"
            : "contents"
        }
      >
      {preview && (
        <div className="hidden min-w-0 flex-1 flex-col border-r border-brown-100 bg-cream-50 lg:flex">
          <p className="border-b border-brown-100 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-brown-300">
            What the client will see
          </p>
          <div ref={setPreviewPane} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            {/* Drawn at a laptop-screen width and scaled to fill the pane
                exactly — no blank strip (Stephanie 2026-10-27) — so it looks
                like the client's page rather than its narrow phone layout. */}
            <div
              className="pointer-events-none select-none"
              style={{ width: PREVIEW_WIDTH, zoom: previewZoom }}
              aria-hidden="true"
            >
              {preview}
            </div>
          </div>
        </div>
      )}
      <div
        className={
          preview
            ? "w-full max-w-lg shrink-0 overflow-y-auto"
            : "max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-brown-100 bg-white shadow-xl"
        }
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-brown-50 px-6 py-4">
          <div>
            <h2
              id="send-customer-modal-title"
              className="font-display text-2xl font-semibold text-brown-500"
            >
              {title}
            </h2>
            <p className="mt-1 text-sm text-brown-300">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-brown-300 hover:text-brown-500 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 py-5">
          {addressMode ? (
            <>
              <AddressField label="To" entries={toList} onChange={setToList} book={book} taken={takenEmails} disabled={submitting} draft={drafts.to} onDraftChange={setDraft("to")} externalError={draftErrors.to ?? null} />
              <AddressField label="CC" entries={ccList} onChange={setCcList} book={book} taken={takenEmails} disabled={submitting} draft={drafts.cc} onDraftChange={setDraft("cc")} externalError={draftErrors.cc ?? null} />
              <div>
                <AddressField label="BCC" entries={bccList} onChange={setBccList} book={book} taken={takenEmails} disabled={submitting} draft={drafts.bcc} onDraftChange={setDraft("bcc")} externalError={draftErrors.bcc ?? null} />
                <div className="mt-2 space-y-1.5">
                  {composeQ.data?.production_email && (
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-brown-500">
                      <input
                        type="checkbox"
                        checked={bccProduction}
                        onChange={(e) => setBccProduction(e.target.checked)}
                        disabled={submitting}
                        className="h-4 w-4 rounded border-brown-200 text-ink focus:ring-peach-300"
                      />
                      Production ({composeQ.data.production_email})
                    </label>
                  )}
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-brown-500">
                    <input
                      type="checkbox"
                      checked={copyToMe}
                      onChange={(e) => setCopyToMe(e.target.checked)}
                      disabled={submitting}
                      className="h-4 w-4 rounded border-brown-200 text-ink focus:ring-peach-300"
                    />
                    Me{currentUserEmail ? ` (${currentUserEmail})` : ""}
                  </label>
                </div>
              </div>
              {templatesQ.data?.available && templatesQ.data.templates.length > 0 && (
                <div>
                  <label
                    htmlFor="send-po-template"
                    className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
                  >
                    Template
                  </label>
                  <select
                    id="send-po-template"
                    value={templateId}
                    onChange={(e) => void applyTemplate(e.target.value)}
                    disabled={submitting || templateLoading || !addressSeeded}
                    className="h-9 w-full rounded-md border border-brown-100 bg-white px-3 text-sm text-brown-500"
                  >
                    <option value="">Standard email</option>
                    {templatesQ.data.templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {templateError && <p className="mt-1 text-xs text-rust-600">{templateError}</p>}
                </div>
              )}
              <div>
                <label
                  htmlFor="send-po-subject"
                  className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
                >
                  Subject
                </label>
                <Input
                  id="send-po-subject"
                  value={subject ?? ""}
                  onChange={(e) => setSubject(e.target.value.replace(/[\r\n]/g, " "))}
                  maxLength={200}
                  placeholder={addressSeeded ? "" : "Loading…"}
                  className="h-9 text-sm"
                  disabled={submitting || !addressSeeded}
                />
              </div>
              {/* The artwork goes as attachments (Stephanie + Jake
                  2026-10-15); the PO itself stays a link. */}
              {(composeQ.data?.artwork_files?.length ?? 0) > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
                    Artwork attached
                  </p>
                  <ul className="space-y-1">
                    {composeQ.data!.artwork_files!.map((f) => (
                      <li key={f.file_id} className={`flex items-center gap-2 text-sm ${leaveOff.has(f.file_id) ? "text-muted" : "text-brown-500"}`}>
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-brown-300" />
                        <span className={`truncate ${leaveOff.has(f.file_id) ? "line-through" : ""}`}>{f.filename}</span>
                        <span className="shrink-0 text-xs text-brown-300">{formatFileSize(f.size_bytes)}</span>
                        {!f.attached && (
                          <span className="shrink-0 text-xs text-rust-600">too big to attach — they get the download link</span>
                        )}
                        {f.attached && (
                          <AttachToggle off={leaveOff.has(f.file_id)} name={f.filename} disabled={submitting} onToggle={() => toggleLeaveOff(f.file_id)} />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(composeQ.data?.picture_files?.length ?? 0) > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
                    Virtuals attached
                  </p>
                  <ul className="space-y-1">
                    {composeQ.data!.picture_files!.map((f) => (
                      <li key={f.filename} className={`flex items-center gap-2 text-sm ${leaveOff.has(f.filename) ? "text-muted" : "text-brown-500"}`}>
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-brown-300" />
                        <span className={`truncate ${leaveOff.has(f.filename) ? "line-through" : ""}`}>{f.filename}</span>
                        <AttachToggle off={leaveOff.has(f.filename)} name={f.filename} disabled={submitting} onToggle={() => toggleLeaveOff(f.filename)} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
          <>
          {/* Recipients — vendor presets (PO send) OR client contacts. */}
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              {contactsLabel ?? (vendorMode ? "Recipients" : "Client contacts")}
            </label>
            {vendorMode ? (
              presets.length === 0 ? (
                <p className="rounded-md border border-brown-100 bg-cream-50/40 px-3 py-2 text-sm text-brown-300">
                  No supplier email on file. Add a recipient below.
                </p>
              ) : (
                <ul className="max-h-52 overflow-y-auto rounded-md border border-brown-100 bg-cream-50/40">
                  {presets.map((p) => {
                    const key = p.email.toLowerCase();
                    const checked = selectedPresets.has(key);
                    return (
                      <li
                        key={key}
                        className="border-b border-brown-50 last:border-0"
                      >
                        <label className="flex cursor-pointer items-start gap-2 px-3 py-2 text-sm text-brown-500">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = new Set(selectedPresets);
                              if (e.target.checked) next.add(key);
                              else next.delete(key);
                              setSelectedPresets(next);
                            }}
                            className="mt-0.5 h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
                            disabled={submitting}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="font-medium">{p.label}</span>
                            {p.sublabel && (
                              <span className="block text-xs text-brown-300">
                                {p.sublabel}
                              </span>
                            )}
                            <span className="block truncate text-xs text-brown-400">
                              {p.email}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )
            ) : contactsQ.isLoading ? (
              <p className="text-sm text-brown-300">Loading contacts…</p>
            ) : emailable.length === 0 ? (
              <p className="rounded-md border border-brown-100 bg-cream-50/40 px-3 py-2 text-sm text-brown-300">
                No contacts with an email address. Add a recipient below.
              </p>
            ) : (
              <ul className="max-h-52 overflow-y-auto rounded-md border border-brown-100 bg-cream-50/40">
                {emailable.map((c) => {
                  const checked = selected.has(c.id);
                  return (
                    <li key={c.id} className="border-b border-brown-50 last:border-0">
                      <label className="flex cursor-pointer items-start gap-2 px-3 py-2 text-sm text-brown-500">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(c.id);
                            else next.delete(c.id);
                            setSelected(next);
                          }}
                          className="mt-0.5 h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
                          disabled={submitting}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="font-medium">{contactLabel(c)}</span>
                            {c.isPrimary && (
                              <span className="rounded-full bg-peach-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500">
                                Primary
                              </span>
                            )}
                          </span>
                          {c.title && (
                            <span className="block text-xs text-brown-300">
                              {c.title}
                            </span>
                          )}
                          <span className="block truncate text-xs text-brown-400">
                            {c.email}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Extra emails (chips) */}
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              {extrasLabel}
            </label>
            {extras.length > 0 && (
              <ul className="mb-2 flex flex-wrap gap-2">
                {extras.map((e, i) => (
                  <li
                    key={`${e.email}-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-peach-300 bg-peach-100 px-2.5 py-1 text-xs text-brown-500"
                  >
                    <span className="font-medium">
                      {e.name.length > 0 ? `${e.name} · ${e.email}` : e.email}
                    </span>
                    {allowAddContact && (
                      <label
                        className="flex cursor-pointer items-center gap-1 border-l border-peach-300 pl-1.5 text-brown-400 hover:text-brown-500"
                        title="Save this person as a contact on this client"
                      >
                        <input
                          type="checkbox"
                          checked={addContactIdx === i}
                          onChange={(ev) =>
                            setAddContactIdx(ev.target.checked ? i : null)
                          }
                          disabled={submitting}
                          className="h-3 w-3 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
                        />
                        <UserPlus className="h-3 w-3" /> Add as contact
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setExtras((prev) => prev.filter((_, idx) => idx !== i))
                      }
                      disabled={submitting}
                      className="text-brown-300 hover:text-rust-500 disabled:opacity-50"
                      aria-label={`Remove ${e.email}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  if (addError) setAddError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addExtra();
                  }
                }}
                placeholder={vendorMode ? "orders@supplier.com" : "name@client.com"}
                className="h-9 flex-1 text-sm"
                disabled={submitting}
              />
              <Input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addExtra();
                  }
                }}
                placeholder="Name (optional)"
                maxLength={200}
                className="h-9 w-40 text-sm"
                disabled={submitting}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addExtra}
                disabled={submitting || newEmail.trim().length === 0}
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
            {addError && (
              <p className="mt-1 text-xs text-rust-600">{addError}</p>
            )}
            {/* One authored email goes to EVERY recipient (item 5). It lives
                here rather than concatenated onto the header subtitle: still in
                the rep's eye-line while they choose recipients — which is when
                it matters — contextually adjacent to the list it describes, and
                the header returns to a single line. */}
            {showBodyEditor && (
              <p className="mt-1.5 text-[11px] text-brown-300">{sendNote}</p>
            )}
          </div>

          {/* Send me a copy — adds the logged-in rep as an extra recipient. */}
          {showCopyToMe && (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-brown-500">
              <input
                type="checkbox"
                checked={copyToMe}
                onChange={(e) => setCopyToMe(e.target.checked)}
                disabled={submitting}
                className="h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
              />
              Send me a copy
            </label>
          )}

          </>
          )}

          {/* Full email body — EVERY word editable (CEO 2026-08-04). Pre-filled
              from the server with the exact default email; nothing is appended
              below it. "Add link" attaches the share URL to the selected words;
              "Copy link" hands over the raw URL to paste anywhere. */}
          {!addressMode && editableSubject && (
            <div>
              <label
                htmlFor="send-subject"
                className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
              >
                Subject
              </label>
              <Input
                id="send-subject"
                value={subject ?? ""}
                onChange={(e) => setSubject(e.target.value.replace(/[\r\n]/g, " "))}
                maxLength={200}
                placeholder={subject === null ? "Loading…" : ""}
                className="h-9 text-sm"
                disabled={submitting || subject === null}
              />
            </div>
          )}
          {showBodyEditor && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label
                  htmlFor="send-body"
                  className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
                >
                  {bodyLabel}
                </label>
                {bodyDirty && (
                  <button
                    type="button"
                    onClick={resetBody}
                    disabled={submitting}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-ink underline underline-offset-4 hover:text-muted disabled:opacity-50"
                  >
                    <RotateCcw className="h-3 w-3" /> Reset to default
                  </button>
                )}
              </div>

              {/* COMPOSE FAILURE (item 1). Without this branch a failed fetch is
                  a silent permanent dead end: isLoading goes false so the
                  caption reads like everything is fine, the editor sits blank,
                  bodySeeded never flips and Send is disabled forever with no
                  stated reason. Say what broke and offer the way out. */}
              {composeQ.isError && (
                <div className="rounded-lg border border-rust-200 bg-rust-50 px-3 py-2.5">
                  <p className="text-sm font-semibold text-rust-600">
                    {BLOCK_COPY.composeFailed}
                  </p>
                  <p className="mt-0.5 text-xs text-brown-400">
                    Nothing was sent. Check your connection and try again — or
                    close this and use Copy link to share the{" "}
                    {linkNoun} directly.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    disabled={composeQ.isFetching || submitting}
                    onClick={() => void composeQ.refetch()}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />{" "}
                    {composeQ.isFetching ? "Retrying…" : "Retry"}
                  </Button>
                </div>
              )}

              {/* Who the client will see this FROM (item 6 / CEO note 1). The
                  whole point of note 1 was the From address, and it was
                  invisible here. It also matters operationally: the sender is
                  the ACCOUNT REP, so someone sending on a colleague's document
                  needs to see whose name goes out. Resolved server-side by the
                  same code path that sets the real header, noreply@ fallback
                  included, so this line can never disagree with reality. */}
              {composeQ.data && (
                <p className="mb-1 text-[11px] text-brown-300">
                  Sending as{" "}
                  <span className="font-semibold text-brown-400">
                    {composeQ.data.from_name}
                  </span>{" "}
                  · {composeQ.data.from_email}
                  {sendingAsReason && (
                    <span className="text-brown-300"> ({sendingAsReason})</span>
                  )}
                </p>
              )}
              {!addressMode && composeQ.data?.art_cc && (
                <p className="mb-1 text-[11px] text-brown-300">
                  CC <span className="font-semibold text-brown-400">art team</span> ·{" "}
                  {composeQ.data.art_cc}
                </p>
              )}

              {/* Toolbar. onMouseDown preventDefault keeps the text selection
                  alive while the button takes focus — without it every command
                  would apply to an empty selection. */}
              <div
                className={
                  composeQ.isError
                    ? "hidden"
                    : "flex flex-wrap items-center gap-1 rounded-t-lg border border-b-0 border-brown-200 bg-cream-50/60 px-2 py-1.5"
                }
              >
                {(
                  [
                    { cmd: "bold", Icon: Bold, label: "Bold" },
                    { cmd: "italic", Icon: Italic, label: "Italic" },
                    { cmd: "underline", Icon: Underline, label: "Underline" },
                    {
                      cmd: "insertUnorderedList",
                      Icon: List,
                      label: "Bulleted list",
                    },
                    {
                      cmd: "insertOrderedList",
                      Icon: ListOrdered,
                      label: "Numbered list",
                    },
                  ] as const
                ).map(({ cmd, Icon, label }) => {
                  const on = activeMarks[cmd] === true;
                  return (
                    <button
                      key={cmd}
                      type="button"
                      title={label}
                      aria-label={label}
                      aria-pressed={on}
                      disabled={submitting}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => exec(cmd)}
                      className={
                        on
                          ? "rounded bg-peach-200 p-1.5 text-brown-500 disabled:opacity-50"
                          : "rounded p-1.5 text-brown-400 hover:bg-peach-100 hover:text-brown-500 disabled:opacity-50"
                      }
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  );
                })}
                <span className="mx-1 h-4 w-px bg-brown-100" />
                <button
                  type="button"
                  title={`Attach the ${linkNoun} to the selected words`}
                  disabled={submitting || shareUrl.length === 0}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={attachLink}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-peach-100 hover:text-brown-500 disabled:opacity-50"
                >
                  <Link2 className="h-3.5 w-3.5" /> Add link
                </button>
                <button
                  type="button"
                  title={`Copy the ${linkNoun} to your clipboard`}
                  disabled={submitting || shareUrl.length === 0}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={copyLink}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-peach-100 hover:text-brown-500 disabled:opacity-50"
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5" /> Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" /> Copy link
                    </>
                  )}
                </button>
                {/* The formatting promise needs a real hover target. It used to
                    sit on this toolbar's container, but every child button
                    carries its own title and browsers show the INNERMOST one —
                    so it only fired on a few pixels of padding. An icon at the
                    end of the row costs no vertical space (the dialog is
                    already near max-h-[90vh]) and is actually reachable. */}
                <span
                  className="ml-auto inline-flex cursor-help items-center text-brown-300 hover:text-brown-400"
                  title={FORMATTING_CAPTION}
                  aria-label={FORMATTING_CAPTION}
                  tabIndex={0}
                  role="note"
                >
                  <Info className="h-3.5 w-3.5" />
                </span>
              </div>

              <div
                id="send-body"
                ref={editorRef}
                role="textbox"
                aria-multiline="true"
                aria-label={bodyLabel}
                contentEditable={!submitting}
                suppressContentEditableWarning
                onInput={() => {
                  setBodyDirty(true);
                  syncEditorState();
                }}
                onPaste={onPaste}
                onKeyUp={refreshMarks}
                onMouseUp={refreshMarks}
                onFocus={refreshMarks}
                className={
                  composeQ.isError
                    ? "hidden"
                    : "send-body-editor block max-h-72 min-h-[11rem] w-full overflow-y-auto rounded-b-lg border border-brown-200 bg-white px-3 py-2 text-sm font-normal normal-case leading-relaxed text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-300"
                }
              />

              <div
                className={
                  composeQ.isError
                    ? "hidden"
                    : "mt-1 flex items-start justify-between gap-3"
                }
              >
                <p className="text-[11px] text-brown-300">
                  {composeQ.isLoading
                    ? BLOCK_COPY.composeLoading
                    : (linkHint ??
                      "This is the whole email — edit any of it. Select a word and press Add link to point it at the document.")}
                </p>
                {bodyOverLimit && (
                  <span className="shrink-0 text-[11px] font-semibold text-rust-600">
                    {BLOCK_COPY.bodyTooLong}
                  </span>
                )}
              </div>
              {/* What formatting actually survives (item 8) — stated plainly so
                  the paste normalisation above is never a surprise. */}
              {/* An emptied body is its own failure, with its own words — the
                  missing-link gate talks about the link, not emptiness. */}
              {bodyBlank && (
                <p className="mt-1 text-[11px] font-semibold text-rust-600">
                  {BLOCK_COPY.bodyEmpty}
                </p>
              )}
            </div>
          )}

          {/* Soft warning (peach, house convention — duplicate-fee precedent)
              vs genuine error (rust). Two slots so a non-blocking "confirm to
              proceed" notice never wears failure styling. */}
          {noticeMessage && (
            <div className="rounded-lg border border-peach-300 bg-peach-100 px-4 py-3 text-sm text-brown-500">
              {noticeMessage}
            </div>
          )}
          {errorMessage && (
            <p className="text-sm text-rust-600">{errorMessage}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-brown-50 px-6 py-3">
          <span
            className={
              blockedReason ? "text-xs text-rust-600" : "text-xs text-brown-300"
            }
          >
            {blockedReason ??
              (recipientCount === 0
                ? copyToMe
                  ? "Sending a copy to you only"
                  : "Pick at least one recipient"
                : `${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`)}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-3 py-1.5 text-sm font-semibold text-ink underline underline-offset-4 hover:text-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="button"
              size="sm"
              variant={ctaVariant}
              disabled={!canSubmit}
              // A disabled primary action must say why (item 1).
              title={blockedReason ?? undefined}
              onClick={handleSubmit}
            >
              <Check className="h-3.5 w-3.5" />{" "}
              {submitting ? "Sending…" : ctaLabel}
            </Button>
          </div>
        </div>
      </div>
      </div>

      {/* "Add this person as a contact" popup — reuses the FULL contact form
          (same required fields the rep sees under client contacts), pre-filled
          with the typed email. Saving creates the contact and pre-checks it in
          the list above; cancelling leaves it a plain one-off email. */}
      {allowAddContact &&
        addContactIdx !== null &&
        extras[addContactIdx] && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-brown-500/50 px-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setAddContactIdx(null);
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Add as contact"
          >
            <div className="w-full max-w-lg">
              <ContactForm
                clientId={clientId}
                heading="Add as contact"
                initialEmail={extras[addContactIdx].email}
                initialFirst={splitName(extras[addContactIdx].name).first}
                initialLast={splitName(extras[addContactIdx].name).last}
                onCreated={onContactCreated}
                onCancel={() => setAddContactIdx(null)}
              />
            </div>
          </div>
        )}
    </div>
  );
}

/** ✕ to leave an attachment off this email; "Add back" to undo (Stephanie 2026-11-02). */
function AttachToggle({ off, name, disabled, onToggle }: { off: boolean; name: string; disabled: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className="ml-auto shrink-0 text-xs font-medium text-muted hover:text-ink"
      aria-label={off ? `Attach ${name} again` : `Don't attach ${name}`}
      title={off ? "Attach it again" : "Don't attach this to the email"}
    >
      {off ? "Add back" : "✕"}
    </button>
  );
}
